import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { fetchAnsCase } from '@/services/ans';

/**
 * One ANS penalty: the fine at each stage, the GRU, and the board-reviewed
 * cases like it.
 *
 * Every `Answer` here is record-scoped: a refusal on this page means THIS
 * case lacks the substrate (no comparable group, say), never that ANS cannot
 * support the analysis -- which is the corpus-level claim the badge would
 * make by default. The comparables are group-shaped: RN 489 tabulates the
 * fine per infraction, so the cases sharing this one's track, tabulated
 * amount and first-decision year are its comparables, and the panel says how
 * the board treated them, never what it will do here.
 */
export function AnsCaseDetailPage() {
  const { key = '' } = useParams();
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'case', key],
    queryFn: () => fetchAnsCase(key),
    enabled: key.length > 0,
    // A 404 is an answer (no such NR_DEMANDA), not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const money = (value: number | null) => (value === null ? '—' : currency.format(value));

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('ans:case.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const c = data.case;
  const r = data.record;

  const facts: [string, string][] = [
    [t('ans:case.facts.process'), c.process_number ?? '—'],
    [t('ans:case.facts.track'), c.track ?? '—'],
    [t('ans:case.facts.object'), c.object ?? '—'],
    [t('ans:case.facts.penaltyType'), r.penalty_type ?? '—'],
    [t('ans:case.facts.status'), c.case_status ?? '—'],
    [t('ans:case.facts.registro'), c.operator_registro ?? '—'],
    [t('ans:case.facts.firstDecision'), c.first_decision_date ?? '—'],
    [t('ans:case.facts.firstPublication'), r.first_publication_date ?? '—'],
    [t('ans:case.facts.boardDecision'), c.board_decision_date ?? '—'],
    [t('ans:case.facts.boardPublication'), r.board_publication_date ?? '—'],
    [t('ans:case.facts.closure'), r.closure_date ?? '—'],
    [t('ans:case.facts.closureReason'), r.closure_reason ?? '—'],
    [t('ans:case.facts.competence'), r.source_competence],
    [
      t('ans:case.facts.nip'),
      r.nip_demand_matches ? t('ans:case.facts.nipYes') : t('ans:case.facts.nipNo'),
    ],
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/ans/cases" className="text-primary underline-offset-2 hover:underline">
            {t('ans:case.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold tabular-nums text-foreground">
          {t('ans:case.title', { demand: c.notice_number })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {c.entity_cnpj ? (
            <Link
              to={`/ans/e/${c.entity_cnpj}`}
              className="text-primary underline-offset-2 hover:underline"
            >
              {c.entity_name ?? c.entity_cnpj}
            </Link>
          ) : (
            (c.entity_name ?? '—')
          )}
        </p>
      </header>

      <ExplainerCard i18nKey="ans:case.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-facts>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ans:case.facts.title')}</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 border-b py-1">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-right text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
        {r.source_row_count > 1 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('ans:case.facts.doubled', {
              n: number.format(r.source_row_count),
            })}
          </p>
        ) : null}
      </section>

      <section className="rounded-lg border bg-card p-4" data-walk>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ans:case.walk.title')}</h2>
        <Answer answer={data.penalty_walk} scope="record" help={t('ans:case.walk.help')}>
          {(walk) => (
            <div className="space-y-3">
              <ol className="grid gap-3 sm:grid-cols-3">
                {walk.stages.map((stage) => (
                  <li
                    key={stage.stage}
                    className="rounded-md border bg-muted/30 p-3"
                    data-stage={stage.stage}
                  >
                    <div className="text-xs text-muted-foreground">
                      {t(`ans:case.walk.stages.${stage.stage}`)}
                    </div>
                    <div className="mt-1 text-base font-semibold tabular-nums text-foreground">
                      {money(stage.amount)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stage.stage_date ?? t('ans:case.walk.undated')}
                    </div>
                    {stage.label ? (
                      <div className="mt-1 text-xs text-foreground">{stage.label}</div>
                    ) : null}
                  </li>
                ))}
              </ol>
              <p className="text-xs text-muted-foreground">
                {t('ans:case.walk.trajectory', {
                  trajectory: walk.trajectory
                    ? t(`ans:cases.trajectories.${walk.trajectory}`, {
                        defaultValue: walk.trajectory,
                      })
                    : '—',
                  retained:
                    walk.retained_share === null ? '—' : percent.format(walk.retained_share),
                })}
                {walk.discount_total !== null
                  ? ` ${t('ans:case.walk.discount', { amount: currency.format(walk.discount_total) })}`
                  : ''}
              </p>
              {!walk.board_reviewed ? (
                <p className="text-xs text-muted-foreground">{t('ans:case.walk.notReviewed')}</p>
              ) : !walk.board_dated ? (
                <p className="text-xs text-muted-foreground">{t('ans:case.walk.undatedBoard')}</p>
              ) : null}
              {walk.final_disagrees_with_source ? (
                <p className="text-xs text-muted-foreground" data-final-disagrees>
                  {t('ans:case.walk.disagrees', {
                    derived: money(r.final_amount_derived),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:case.guide.walk" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-collection>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:case.collection.title')}
        </h2>
        <Answer answer={data.collection} scope="record" help={t('ans:case.collection.help')}>
          {(k) => (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {(
                [
                  ['creditStatus', k.credit_status ?? '—'],
                  ['gru', k.gru_number ?? '—'],
                  ['dueDate', k.due_date ?? '—'],
                  ['amountDue', money(k.amount_due)],
                  ['paymentDate', k.payment_date ?? '—'],
                  ['paidLump', money(k.amount_paid_lump)],
                  ['paidInstallments', money(k.amount_paid_installments)],
                  ['paidTotal', money(k.amount_paid_total)],
                  [
                    'installmentStatus',
                    k.installment_statuses.length ? k.installment_statuses.join(', ') : '—',
                  ],
                  [
                    'dividaAtiva',
                    k.divida_ativa === null
                      ? '—'
                      : k.divida_ativa
                        ? `${t('ans:case.collection.yes')} ${k.divida_ativa_date ?? ''}`
                        : t('ans:case.collection.no'),
                  ],
                  ['paymentOrigin', k.payment_origin ?? '—'],
                  ['daysLate', k.days_late === null ? '—' : number.format(k.days_late)],
                  ['judicialSuspension', k.judicial_suspension_date ?? '—'],
                  ['adminSuspension', k.admin_suspension_date ?? '—'],
                ] as const
              ).map(([field, value]) => (
                <div
                  key={field}
                  className="flex justify-between gap-3 border-b py-1"
                  data-field={field}
                >
                  <dt className="text-muted-foreground">
                    {t(`ans:case.collection.fields.${field}`)}
                  </dt>
                  <dd className="text-right text-foreground">{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-comparables>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:case.comparables.title')}
        </h2>
        <Answer answer={data.comparables} scope="record" help={t('ans:case.comparables.help')}>
          {(g) => (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {t(`ans:case.comparables.basis.${g.basis}`, {
                  track: g.track,
                  object: g.group_object ?? '—',
                  amount: g.group_amount === null ? '—' : currency.format(g.group_amount),
                  year: g.decision_year,
                })}
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                {(
                  [
                    ['members', g.member_count],
                    ['maintained', g.maintained],
                    ['reduced', g.reduced],
                    ['increased', g.increased],
                    ['eliminated', g.eliminated],
                  ] as const
                ).map(([field, value]) => (
                  <div key={field} className="rounded-md border bg-muted/30 p-2" data-group={field}>
                    <div className="text-xs text-muted-foreground">
                      {t(`ans:case.comparables.${field}`)}
                    </div>
                    <div className="font-semibold tabular-nums text-foreground">
                      {number.format(value)}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('ans:case.comparables.reducedShare', {
                  share: percent.format(g.reduced_share),
                })}
              </p>
              <ul className="flex flex-wrap gap-2 text-xs">
                {Object.entries(g.outcome_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([outcome, count]) => (
                    <li key={outcome} className="rounded border px-2 py-1 text-muted-foreground">
                      {outcome}:{' '}
                      <span className="tabular-nums text-foreground">{number.format(count)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:case.guide.comparables" />
        </div>
      </section>
    </div>
  );
}
